import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Shared helpers for the HR field-tracking pages (live view and day history)

export const STATUS = {
  on_duty: { label: 'On Duty', badge: 'bg-green-100 text-green-800', dot: 'bg-green-600', color: '#16a34a' },
  tracking_interrupted: { label: 'Tracking interrupted', badge: 'bg-red-100 text-red-800', dot: 'bg-red-600', color: '#dc2626' },
  stale: { label: 'Offline / stale', badge: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500', color: '#d97706' },
  off_duty: { label: 'Off Duty', badge: 'bg-zinc-100 text-zinc-600', dot: 'bg-zinc-400', color: '#a1a1aa' },
};

const IST = 'Asia/Kolkata';

export const todayIST = () => new Date().toLocaleDateString('en-CA', { timeZone: IST });

export const formatTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString('en-IN', { timeZone: IST, hour: 'numeric', minute: '2-digit' }) : '—';

export const formatDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString('en-IN', { timeZone: IST, day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '—';

export const minutesBetween = (fromIso, toMs = Date.now()) => Math.max(0, Math.round((toMs - new Date(fromIso).getTime()) / 60000));

export const formatMinutes = (minutes) => {
  if (minutes < 60) return `${Math.round(minutes)} min`;
  return `${Math.floor(minutes / 60)} h ${Math.round(minutes % 60)} min`;
};

export const timeAgo = (iso, now = Date.now()) => {
  if (!iso) return '—';
  const minutes = minutesBetween(iso, now);
  return minutes < 1 ? 'just now' : `${formatMinutes(minutes)} ago`;
};

export const distanceM = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const a = Math.sin(toRad(lat2 - lat1) / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(toRad(lon2 - lon1) / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/** The approved site a position is inside, using each site's own radius */
export const siteAt = (lat, lng, locations) => {
  let best = null;
  let bestDistance = Infinity;
  for (const loc of locations || []) {
    const d = distanceM(lat, lng, loc.latitude, loc.longitude);
    if (d <= (loc.radius || 100) && d < bestDistance) {
      best = loc;
      bestDistance = d;
    }
  }
  return best;
};

export const coords = (lat, lng) => `${lat.toFixed(5)}, ${lng.toFixed(5)}`;

export const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const INDIA_CENTER = [22.5, 78.9];

/** Creates an OpenStreetMap-backed Leaflet map in the returned ref's element */
export function useLeafletMap() {
  const containerRef = useRef(null);
  const [map, setMap] = useState(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const instance = L.map(containerRef.current, { zoomControl: true }).setView(INDIA_CENTER, 5);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(instance);
    setMap(instance);
    return () => instance.remove();
  }, []);

  return [containerRef, map];
}

/** offset: draw the badge beside its point instead of on it (start/end often coincide with stop 1) */
export const numberedIcon = (label, background, offset = false) => L.divIcon({
  className: '',
  html: `<div style="background:${background};color:#fff;width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:12px;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)">${escapeHtml(label)}</div>`,
  iconSize: [26, 26],
  iconAnchor: offset ? [30, 30] : [13, 13],
});

export const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
